import {
  fetchWithErrorHandling,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {announce} from '@github-ui/aria-live'
import {threadPreviewsQueryKey} from '../page-data/loaders/use-thread-previews'
import {threadsQueryKey} from '../page-data/loaders/use-threads'
import {useMarkersActions, usePullRequestBasePath, useUnresolvedConversationCountActions} from '../stores/selectors'
import type {FileKey, LineKey} from '@github-ui/diff-lines/types'
import {pullRequestDismissAutomatedReviewCommentPath} from '@github-ui/paths'
import {DelegatedDismissalRequestStatuses} from '@github-ui/diff-lines/conversation-types'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {Type} from '@github-ui/react-query-persister/typebox'
import {Compile} from '@github-ui/react-query-persister/typebox/compile'

const NullableStringSchema = Type.Union([Type.String(), Type.Null()])
const EffectiveDismissalStateSchema = Type.Object({
  thread: Type.Object({
    isResolved: Type.Boolean(),
    resolvedBy: NullableStringSchema,
  }),
  automatedComment: Type.Object({
    isDismissed: Type.Boolean(),
    dismissalReason: NullableStringSchema,
    dismissedBy: NullableStringSchema,
    delegatedDismissalRequest: Type.Union([
      Type.Object({
        requester: Type.String(),
        reason: NullableStringSchema,
        status: Type.Union([
          Type.Literal(DelegatedDismissalRequestStatuses.Pending),
          Type.Literal(DelegatedDismissalRequestStatuses.Approved),
          Type.Literal(DelegatedDismissalRequestStatuses.Rejected),
          Type.Literal(DelegatedDismissalRequestStatuses.Cancelled),
        ]),
        reviewer: NullableStringSchema,
      }),
      Type.Null(),
    ]),
    viewerCanDismiss: Type.Boolean(),
    viewerCanReopen: Type.Boolean(),
  }),
})

type EffectiveDismissalState = Type.Static<typeof EffectiveDismissalStateSchema>
export type DismissAutomatedReviewThreadResult = {kind: 'legacy'} | {kind: 'effective'; state: EffectiveDismissalState}

const EffectiveDismissalStateValidator = Compile(EffectiveDismissalStateSchema)

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isLegacyDismissalResponse(value: unknown): value is {message: string} {
  return isRecord(value) && typeof value.message === 'string' && !('thread' in value) && !('automatedComment' in value)
}

function isEffectiveDismissalState(value: unknown): value is EffectiveDismissalState {
  return EffectiveDismissalStateValidator.Check(value)
}

export function dismissalResultFromResponse(
  response: unknown,
  useEffectiveState: boolean,
): DismissAutomatedReviewThreadResult {
  if (!useEffectiveState) {
    return {kind: 'legacy'}
  }
  if (isEffectiveDismissalState(response)) {
    return {kind: 'effective', state: response}
  }
  if (isLegacyDismissalResponse(response)) {
    return {kind: 'legacy'}
  }
  throw new Error('Invalid automated review comment dismissal response')
}

export function dismissalResultIsResolved(result: DismissAutomatedReviewThreadResult) {
  return result.kind === 'legacy' || result.state.thread.isResolved
}

export function dismissalResultAnnouncement(result: DismissAutomatedReviewThreadResult) {
  if (result.kind === 'legacy' || result.state.automatedComment.isDismissed) {
    return 'Review comment dismissed.'
  }
  if (result.state.automatedComment.delegatedDismissalRequest?.status === DelegatedDismissalRequestStatuses.Pending) {
    return 'Request to dismiss submitted.'
  }
  return 'Review comment was not dismissed.'
}

export function useDismissAutomatedReviewThreadMutation(filePath: string, lineMarkersKey: LineKey | FileKey) {
  const basePath = usePullRequestBasePath()
  const currentUserLogin = getCurrentUserLogin()
  const queryClient = useQueryClient()
  const {updateThread} = useMarkersActions()
  const {decrementUnresolvedConversationCount} = useUnresolvedConversationCountActions()

  return useMutation({
    mutationFn: async ({
      automatedReviewCommentId,
      reason,
      resolutionNote,
    }: {
      automatedReviewCommentId: string
      reason: string
      resolutionNote?: string
      threadId: string
    }) => {
      const apiUrl = pullRequestDismissAutomatedReviewCommentPath({basePath, automatedReviewCommentId})
      const response = await fetchWithErrorHandling(apiUrl, {
        method: 'PUT',
        body: {
          reason,
          resolution_note: resolutionNote,
        },
      })

      throwErrorsIfBadResponse(response)
      const data: unknown = await response.json()
      return dismissalResultFromResponse(data, isFeatureEnabled('automated_review_comment_effective_dismissal_state'))
    },
    onSuccess: (result, variables) => {
      let resolvedByMutation = false
      updateThread(Number(variables.threadId), filePath, lineMarkersKey, oldThread => {
        if (oldThread?.commentsData?.comments[0]?.automatedComment) {
          const automatedComment = oldThread.commentsData?.comments[0]?.automatedComment
          if (result.kind === 'legacy') {
            resolvedByMutation = !oldThread.isResolved
            return {
              ...oldThread,
              isResolved: true,
              resolvedBy: currentUserLogin,
              commentsData: {
                ...oldThread.commentsData,
                comments: [
                  {
                    ...oldThread.commentsData.comments[0],
                    automatedComment: {
                      ...automatedComment,
                      isDismissed: true,
                      viewerCanDismiss: false,
                      viewerCanReopen: true,
                    },
                  },
                  ...oldThread.commentsData.comments.slice(1),
                ],
              },
            }
          }

          const data = result.state
          const delegatedDismissalRequest = data.automatedComment.delegatedDismissalRequest
          resolvedByMutation = !oldThread.isResolved && data.thread.isResolved
          return {
            ...oldThread,
            isResolved: data.thread.isResolved,
            resolvedBy: data.thread.resolvedBy ?? undefined,
            commentsData: {
              ...oldThread.commentsData,
              comments: [
                {
                  ...oldThread.commentsData.comments[0],
                  automatedComment: {
                    ...automatedComment,
                    isDismissed: data.automatedComment.isDismissed,
                    dismissalReason: data.automatedComment.dismissalReason ?? undefined,
                    dismissedBy: data.automatedComment.dismissedBy ?? undefined,
                    delegatedDismissalRequest: delegatedDismissalRequest
                      ? {
                          ...delegatedDismissalRequest,
                          reason: delegatedDismissalRequest.reason ?? undefined,
                          reviewer: delegatedDismissalRequest.reviewer ?? undefined,
                        }
                      : undefined,
                    viewerCanDismiss: data.automatedComment.viewerCanDismiss,
                    viewerCanReopen: data.automatedComment.viewerCanReopen,
                  },
                },
                ...oldThread.commentsData.comments.slice(1),
              ],
            },
          }
        }
      })

      if (resolvedByMutation) {
        decrementUnresolvedConversationCount()
      }

      announce(dismissalResultAnnouncement(result))

      queryClient.invalidateQueries({
        queryKey: result.kind === 'effective' ? threadsQueryKey(basePath) : threadPreviewsQueryKey(basePath),
      })
    },
  })
}
